import {
  PdfSize,
  PdfUnit,
  PdfPen,
  PdfBrush,
  PdfColor,
  PdfFont,
  PdfPoint,
  PdfRect,
  PdfSize as Size,
} from './pdf';
import { Random } from '@educraft/base';

export class GUtil {
  public static boldPen: PdfPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 3);
  public static basePen: PdfPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
  public static cutPen: PdfPen = (() => {
    const pen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.2);
    pen.dashArray = [1, 2];
    return pen;
  })();
  public static writePen: PdfPen = (() => {
    const pen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
    pen.dashArray = [3, 1];
    return pen;
  })();
  public static foldPen: PdfPen = (() => {
    const pen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
    pen.dashArray = [5, 5];
    return pen;
  })();
  public static outlinePen: PdfPen = new PdfPen(PdfColor.fromRgb(255, 255, 255), 0.5);

  public static questionBrush: PdfBrush = new PdfBrush(PdfColor.fromRgb(230, 230, 255));
  public static answerBrush: PdfBrush = new PdfBrush(PdfColor.fromRgb(230, 255, 230));

  public static baseFont: PdfFont = new PdfFont(PdfFont.FONT_NAME, 24);
  public static exerciseFont: PdfFont = new PdfFont(PdfFont.FONT_NAME, 14);

  public static circleHandleSize: number = PdfUnit.FromMillimeters(4);

  public static piccoloSize: PdfSize = new PdfSize(
    PdfUnit.FromMillimeters(164),
    PdfUnit.FromMillimeters(224)
  );
  public static primoSize: PdfSize = new PdfSize(
    PdfUnit.FromMillimeters(162),
    PdfUnit.FromMillimeters(224)
  );
  public static maximoSize: PdfSize = new PdfSize(
    PdfUnit.FromMillimeters(175),
    PdfUnit.FromMillimeters(236)
  );
  public static lcdTableSize: PdfSize = new PdfSize(
    PdfUnit.FromMillimeters(122),
    PdfUnit.FromMillimeters(193)
  );

  public static piccoloColors: PdfColor[] = [
    PdfColor.fromCmyk(0, 1, 1, 0),
    PdfColor.fromCmyk(0, 0.6, 1, 0),
    PdfColor.fromCmyk(0, 0, 1, 0),
    PdfColor.fromCmyk(1, 0, 1, 0),
    PdfColor.fromCmyk(1, 0, 0, 0),
  ];

  public static primoColors: PdfColor[] = [
    PdfColor.fromCmyk(0, 1, 1, 0),
    PdfColor.fromCmyk(0, 0.6, 1, 0),
    PdfColor.fromCmyk(0, 0, 1, 0),
    PdfColor.fromCmyk(1, 0, 1, 0),
    PdfColor.fromCmyk(1, 0, 0, 0),
    PdfColor.fromCmyk(0, 0.6, 0, 0),
  ];

  public static maximoColors: PdfColor[] = [
    PdfColor.fromCmyk(0.0, 1.0, 1.0, 0.0),
    PdfColor.fromCmyk(0.0, 0.6, 1.0, 0.0),
    PdfColor.fromCmyk(0.0, 0.0, 1.0, 0.0),
    PdfColor.fromCmyk(1.0, 0.0, 1.0, 0.0),
    PdfColor.fromCmyk(1.0, 0.0, 1.0, 0.6),
    PdfColor.fromCmyk(1.0, 0.0, 0.0, 0.0),
    PdfColor.fromCmyk(1.0, 1.0, 0.0, 0.0),
    PdfColor.fromCmyk(0.0, 0.2, 0.2, 0.6),
    PdfColor.fromCmyk(0.0, 0.6, 0.0, 0.0),
  ];

  // MiniLUK uses an "alpha" as first argument in the C# source; map to opacity
  public static miniLUKColors: PdfColor[] = [
    PdfColor.fromCmyk(0, 1, 1, 0, 0.2),
    PdfColor.fromCmyk(1, 0, 1, 0, 0.2),
    PdfColor.fromCmyk(0, 0, 1, 0, 0.2),
    PdfColor.fromCmyk(1, 0, 0, 0, 0.2),
    PdfColor.fromCmyk(0, 0.6, 1, 0, 0.2),
    PdfColor.fromCmyk(0, 0.6, 0, 0, 0.2),
  ];

  public static geoBoardColors: PdfColor[] = [
    PdfColor.fromCmyk(0, 1, 1, 0),
    PdfColor.fromCmyk(0.0, 0.6, 1.0, 0.0),
    PdfColor.fromCmyk(0, 0, 1, 0),
    PdfColor.fromCmyk(1, 0, 1, 0),
    PdfColor.fromCmyk(1, 1, 0, 0),
  ];

  public static pastelColors: PdfColor[] = [
    PdfColor.fromRgb(211, 193, 195),
    PdfColor.fromRgb(226, 208, 190),
    PdfColor.fromRgb(238, 229, 191),
    PdfColor.fromRgb(232, 248, 193),
    PdfColor.fromRgb(209, 255, 198),
  ];

  // Color palettes for Behacka and similar generators
  public static behackaColorPalettes: string[][] = [
    [
      '#7c309c',
      '#88499e',
      '#945f9f',
      '#a074a0',
      '#ac88a0',
      '#b99b9e',
      '#c5af9c',
      '#d2c398',
      '#dfd692',
      '#ecea8b',
    ],
    [
      '#cad49d',
      '#becb84',
      '#b3c269',
      '#b2b275',
      '#b69f8e',
      '#b88aa3',
      '#ba72b4',
      '#b664b8',
      '#a86ea2',
      '#99758b',
    ],
    [
      '#f4fc30',
      '#f1f064',
      '#efe583',
      '#ecd99b',
      '#e9cdaf',
      '#e7c1c1',
      '#e4b3d1',
      '#e2a5e0',
      '#e096ee',
      '#de85fb',
    ],
  ];

  // Standard color set for coloring exercises
  public static standardColors: PdfBrush[] = [
    new PdfBrush(PdfColor.fromHex('#FF0000')), // Red
    new PdfBrush(PdfColor.fromHex('#FFA500')), // Orange
    new PdfBrush(PdfColor.fromHex('#FFFF00')), // Yellow
    new PdfBrush(PdfColor.fromHex('#008000')), // Green
    new PdfBrush(PdfColor.fromHex('#0000FF')), // Blue
    new PdfBrush(PdfColor.fromHex('#EE82EE')), // Violet
    new PdfBrush(PdfColor.fromHex('#A52A2A')), // Brown
    new PdfBrush(PdfColor.fromHex('#000000')), // Black
  ];

  // Background brush for alternating rows
  public static backgroundBrush: PdfBrush = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1, 0.5));

  /**
   * Select a random color palette and convert to brushes.
   * Port of C# G<T>.RandomBrushes()
   */
  public static randomBrushes(random: Random, palettes: string[][]): PdfBrush[] {
    const palette = palettes[random.next(0, palettes.length)];
    return palette.map((hex) => new PdfBrush(PdfColor.fromHex(hex)));
  }

  /**
   * Generate a shuffled index map.
   * Port of C# G<T>.GenMap()
   */
  public static genMap(random: Random, count: number): number[] {
    const arr = Array.from({ length: count }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = random.next(0, i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
}

export function safeBase64Encode(data: string | Uint8Array): string {
  let bytes: Uint8Array;
  if (typeof data === 'string') {
    bytes = new TextEncoder().encode(data);
  } else {
    bytes = data;
  }
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
